import React from 'react';
import GPASection from './gpa-section';
import SATSection from './sat-section';
import ACTSection from './act-section';
import ACTTable from './act-table';
import Icon from 'shared/components/icon';
import SATTable from './sat-table';
import ScholarshipCTA from 'shared/college-profile/containers/about-college/scholarship-cta';
import Card from 'src/components/Cards/Card';
import CollapsibleTeaserSection from 'shared/components/collapsible-teaser-section';
import MiniInfoCardContainer from 'shared/components/college-profile/mini-info-card-container';
import MiniInfoCard from 'shared/components/college-profile/mini-info-card';
import NewTabTooltip from 'shared/components/new-tab-tooltip';
import {gradeLevelForGraduationYear} from 'shared/utils';

interface Props {
  college: {
    acceptanceRate?: number | null;
    applicationFee?: number | null;
    satPercentSubmitting?: number | null;
    actPercentSubmitting?: number | null;
    partnerScholarships: $TSFixMe;
    admissionsWebsite?: string | null;
    applicationWebsite?: string | null;
    actEnglish: number[];
    actMath: number[];
    actWriting: number[];
    name: string;
    actComposite?: number[];
    satCriticalReadingRange?: number[];
    satMathematicsRange?: number[];
    averageGPA: number;
  };
  userInfo: {
    highSchoolInfo: {
      graduationYear?: number | null;
      gpa: number;
    };
    actScore?: {
      scoreEnglish: number;
      scoreMath: number;
      scoreWriting: number;
      score: number;
    };
    satScore?: {
      scoreReadingAndWriting: number;
      scoreMathematics: number;
      score: number;
    };
  };
  showGPASection: boolean;
  showTestsSection: boolean;
  showTestScores: boolean;
}

const AboutAdmissions = ({
  college,
  userInfo,
  showGPASection,
  showTestsSection,
  showTestScores,
}: Props) => {
  const admissibility = () => {
    if (!userInfo.highSchoolInfo) {
      return false;
    }

    const gradeLevel = gradeLevelForGraduationYear(userInfo.highSchoolInfo.graduationYear);

    return gradeLevel === 'senior' || gradeLevel === 'junior';
  };

  const showAdmissability = admissibility();

  const {
    acceptanceRate,
    applicationFee,
    satPercentSubmitting,
    actPercentSubmitting,
    partnerScholarships,
    admissionsWebsite,
    applicationWebsite,
  } = college;

  return (
    <Card className="zero-bottom-padding">
      <CollapsibleTeaserSection
        title="Admissions"
        trackIdentifier="college-listing-about-submissons"
        className="college-profile-card-about-content"
      >
        <CollapsibleTeaserSection.Teaser>
          <MiniInfoCardContainer>
            <MiniInfoCard
              iconType="envelope"
              label="Acceptance Rate"
              value={acceptanceRate ? `${Math.round(acceptanceRate)}%` : undefined}
            />
            <MiniInfoCard
              iconType="money-bill-alt"
              label={
                <span>
                  Application
                  <br />
                  Fee
                </span>
              }
              value={applicationFee ? `$${applicationFee}` : undefined}
            />
            {showTestScores && (
              <MiniInfoCard
                iconType="file-alt"
                label={
                  <span>
                    SAT Scores on
                    <br />
                    Applications
                  </span>
                }
                value={satPercentSubmitting ? `${satPercentSubmitting}%` : undefined}
              />
            )}
            {showTestScores && (
              <MiniInfoCard
                iconType="file-alt"
                label={
                  <span>
                    ACT Scores on
                    <br />
                    Applications
                  </span>
                }
                value={actPercentSubmitting ? `${actPercentSubmitting}%` : undefined}
              />
            )}
          </MiniInfoCardContainer>
          {showGPASection && (
            <div>
              <GPASection
                college={college}
                userInfo={userInfo}
                showAdmissability={showAdmissability}
              />
            </div>
          )}
        </CollapsibleTeaserSection.Teaser>
        <CollapsibleTeaserSection.Body>
          {showTestsSection && (
            <div>
              <SATSection
                college={college}
                userInfo={userInfo}
                showAdmissability={showAdmissability}
              />
              <SATTable
                college={college}
                userInfo={userInfo}
                showAdmissability={showAdmissability}
              />
              <ACTSection
                college={college}
                userInfo={userInfo}
                showAdmissability={showAdmissability}
              />
              <ACTTable
                college={college}
                userInfo={userInfo}
                showAdmissability={showAdmissability}
              />
              <ScholarshipCTA scholarships={partnerScholarships} type="scores" />
            </div>
          )}
          <div className="college-profile-section-link-menu bottom-padding">
            {!!admissionsWebsite && (
              <a
                href={admissionsWebsite}
                target="_blank"
                aria-label="Admissions Site (opens in new tab)"
                rel="noreferrer"
              >
                <Icon iconType="external-link-alt" />
                Admissions Site
              </a>
            )}
            {!!applicationWebsite && (
              <NewTabTooltip>
                <a href={applicationWebsite} target="_blank" rel="noreferrer">
                  <Icon iconType="file-alt" />
                  View Application
                </a>
              </NewTabTooltip>
            )}
          </div>
        </CollapsibleTeaserSection.Body>
      </CollapsibleTeaserSection>
    </Card>
  );
};

export default AboutAdmissions;
